// web/src/pages/client/LeakDetailPage.tsx
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, Link, useLocation, useSearchParams } from "react-router-dom";
import {
  ExternalLink,
  AlertTriangle,
  X,
  ZoomIn,
  ZoomOut,
  Image as ImageIcon,
  FileImage,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Bookmark,
  Building2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { fetchLeakById, savePost, unsavePost, fetchSavedPosts } from "../../api/leaks";
import type { Leak } from "../../types/leak";
import { extractIOCs } from "../../utils/ioc";
import { useHeaderStore } from "../../store/headerStore";
import { resolveAssetUrl } from "../../utils/assets";
import { useAuth } from "../../auth/AuthContext";
import { cleanTitle, getDisplayForum, isUrlscanLeak } from "../../utils/cleaners";
import UpgradeBlurOverlay from "../../components/UpgradeBlurOverlay";
import { isLeakLocked } from "../../utils/subscription";

/* ─── Helpers ───────────────────────────────────────── */

/** Format publication date into "Oct 15, 2026" */
function formatDisplayDate(dateStr?: string | null): string {
  if (!dateStr) return "Unknown";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(dateStr);
  }
}

/** Format URL cleanly and safely */
function sanitizeDisplayUrl(url?: string): string {
  if (!url) return "Not available";
  return url;
}

/** Check if URL has a safe http/https protocol */
function isSafeUrl(url?: string): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/* ─── Main Component ────────────────────────────────── */

export default function LeakDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();
  const { setPageTitleOverride } = useHeaderStore();

  const rankFromParams = parseInt(searchParams.get("rank") || "0", 10);
  const rankFromState = location.state?.leakRank;
  const isLockedFromState = location.state?.isLocked;
  const isFromSearch = Boolean(searchParams.get("search")) || location.state?.fromSearch === true;

  const [leak, setLeak] = useState<Leak | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isSaved, setIsSaved] = useState(false);
  const [togglingSave, setTogglingSave] = useState(false);

  const [urlCopied, setUrlCopied] = useState(false);
  const [contentCopied, setContentCopied] = useState(false);

  const [isContentExpanded, setIsContentExpanded] = useState(false);

  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [activeScreenshotIdx, setActiveScreenshotIdx] = useState(0);
  const [isScreenshotUnavailable, setIsScreenshotUnavailable] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const lightboxContainerRef = useRef<HTMLDivElement>(null);
  const prevZoomRef = useRef(1);

  // Reset lightbox scroll position to start (top: 0, left: 0) on zoom change or when opening
  useEffect(() => {
    if (lightboxContainerRef.current) {
      if ((prevZoomRef.current === 1 && zoomLevel > 1) || zoomLevel === 1 || !isLightboxOpen) {
        lightboxContainerRef.current.scrollTop = 0;
        lightboxContainerRef.current.scrollLeft = 0;
      }
    }
    prevZoomRef.current = zoomLevel;
  }, [zoomLevel, isLightboxOpen, activeScreenshotIdx]);

  // Fetch saved state
  useEffect(() => {
    if (id) {
      fetchSavedPosts()
        .then((res) => {
          setIsSaved(res.results.some((r: any) => r._id === id || r.id === id));
        })
        .catch(console.error);
    }
  }, [id]);

  // Fetch leak details
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setIsScreenshotUnavailable(false);
    setActiveScreenshotIdx(0);
    setIsContentExpanded(false);

    const searchParamVal = searchParams.get("search") || (isFromSearch ? "1" : undefined);
    fetchLeakById(id, searchParamVal ? { search: searchParamVal } : undefined)
      .then((res) => {
        if (!res || (res as any).error) {
          setError((res as any)?.error || "Leak record not found or access denied.");
          setLeak(null);
        } else {
          setLeak(res);
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || err.response?.data?.error || err.message || "Failed to load leak details";
        setError(msg);
        setLeak(null);
      })
      .finally(() => setLoading(false));
  }, [id, isFromSearch, searchParams]);

  // Sticky header scroll listener
  useEffect(() => {
    const scrollContainer = document.querySelector(".main-area > div.overflow-y-auto");
    const handler = () => {
      const scrollPos = scrollContainer ? scrollContainer.scrollTop : (window.scrollY || document.documentElement.scrollTop || 0);
      setIsScrolled(scrollPos > 120);
    };
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handler, { passive: true });
    }
    window.addEventListener("scroll", handler, { passive: true });
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handler);
      }
      window.removeEventListener("scroll", handler);
    };
  }, []);

  // Keyboard navigation for lightbox (ESC to close, +/- zoom)
  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (!isLightboxOpen) return;
      if (e.key === "Escape") {
        setIsLightboxOpen(false);
        setZoomLevel(1);
      } else if (e.key === "+" || e.key === "=") {
        setZoomLevel((z) => Math.min(4, z + 0.25));
      } else if (e.key === "-") {
        setZoomLevel((z) => Math.max(0.25, z - 0.25));
      }
    },
    [isLightboxOpen]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  // Save / Unsave handler
  const handleToggleSave = async () => {
    if (!id || togglingSave) return;
    setTogglingSave(true);
    try {
      if (isSaved) {
        await unsavePost(id);
        setIsSaved(false);
        toast.success("Post removed from saved");
      } else {
        await savePost(id);
        setIsSaved(true);
        toast.success("Post saved to collection");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to update saved post");
    } finally {
      setTogglingSave(false);
    }
  };

  // Copy URL action
  const handleCopyUrl = async () => {
    if (!leak?.source_url) return;
    try {
      await navigator.clipboard.writeText(leak.source_url);
      setUrlCopied(true);
      toast.success("Source URL copied to clipboard");
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      toast.error("Failed to copy URL");
    }
  };

  // Copy Content action
  const handleCopyContent = async () => {
    if (!leak?.content) return;
    try {
      await navigator.clipboard.writeText(leak.content);
      setContentCopied(true);
      toast.success("Raw content copied to clipboard");
      setTimeout(() => setContentCopied(false), 2000);
    } catch {
      toast.error("Failed to copy content");
    }
  };

  // Entitlement & Tier Gating: First 50 free, locked after 50 for free plan
  const effectiveRank = leak?.leak_rank || rankFromState || rankFromParams || 0;
  const isLocked = isLeakLocked(leak, effectiveRank, user, isFromSearch) || (isLockedFromState === true && !["admin", "super_admin", "analyst"].includes(user?.role || ""));

  // Title processing
  const rawTitle = leak?.thread_title || leak?.thread_title_list || leak?.title || (leak?.content ? String(leak.content).slice(0, 80).replace(/\n/g, " ") + "…" : "Untitled Leak");
  const leakTitle = cleanTitle(rawTitle);

  useEffect(() => {
    if (!leakTitle) return;
    setPageTitleOverride(leakTitle.slice(0, 40) + (leakTitle.length > 40 ? "..." : ""));
    return () => setPageTitleOverride(null);
  }, [leakTitle, setPageTitleOverride]);

  // Screenshots collection
  const allScreenshotsRaw = useMemo(() => {
    if (!leak) return [];
    const list: string[] = [];
    if (leak.screenshots && Array.isArray(leak.screenshots)) {
      for (const s of leak.screenshots) {
        if (s && typeof s === "string" && !list.includes(s)) {
          list.push(s);
        }
      }
    }
    if (leak.card_screenshot_path && typeof leak.card_screenshot_path === "string" && !list.includes(leak.card_screenshot_path)) {
      list.unshift(leak.card_screenshot_path);
    }
    if (leak.detail_screenshot_path && typeof leak.detail_screenshot_path === "string" && !list.includes(leak.detail_screenshot_path)) {
      list.push(leak.detail_screenshot_path);
    }
    if (leak.screenshot_path && typeof leak.screenshot_path === "string" && !list.includes(leak.screenshot_path)) {
      list.push(leak.screenshot_path);
    }
    if ((leak as any).screenshot && typeof (leak as any).screenshot === "string" && !list.includes((leak as any).screenshot)) {
      list.push((leak as any).screenshot);
    }
    return list;
  }, [leak]);

  const allScreenshots = useMemo(() => {
    return allScreenshotsRaw
      .map((path) => resolveAssetUrl(path))
      .filter((url): url is string => Boolean(url));
  }, [allScreenshotsRaw]);

  const currentScreenshotUrl = allScreenshots.length > 0 && !isScreenshotUnavailable
    ? allScreenshots[Math.min(activeScreenshotIdx, allScreenshots.length - 1)]
    : null;

  // Extract IOCs (used for domain detection)
  const iocs = useMemo(() => {
    return extractIOCs(leak?.content || "");
  }, [leak?.content]);

  // Date resolution
  const publishedDateStr = leak?.published_at || leak?.published_date || leak?.upload_date_raw || leak?.created_at;

  // Domain resolution (prioritizing leak.domain, with intelligent extraction fallback)
  const resolvedDomain = useMemo(() => {
    if (!leak) return null;
    const directDomain = leak.domain || (leak as any).target_domain || (leak as any).website || (leak as any).site;
    if (directDomain && typeof directDomain === "string" && directDomain.trim() && directDomain !== "None" && directDomain !== "null") {
      return directDomain.trim();
    }
    // Check if domain is extractable from thread title
    const titleCandidates = `${leak.thread_title || ""} ${leak.thread_title_list || ""} ${leak.title || ""}`;
    const domainMatch = titleCandidates.match(/\b([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+(?:com|org|net|edu|gov|io|id|in|it|co|sch\.id|go\.id|ac\.id|[a-zA-Z]{2,})\b/i);
    if (domainMatch) {
      const found = domainMatch[0].toLowerCase();
      if (!found.endsWith(".onion") && !found.includes("t.me")) {
        return found;
      }
    }
    // Check IOC domains
    if (iocs.domains && iocs.domains.length > 0) {
      const valid = iocs.domains.find((d) => !d.endsWith(".onion") && !d.includes("t.me") && !d.includes("telegram"));
      if (valid) return valid.toLowerCase();
    }
    return null;
  }, [leak, iocs.domains]);

  /* ─── Loading Skeleton ────────────────────────────── */
  if (loading) {
    return (
      <div className="leak-detail-page enterprise-detail-view" style={{ minHeight: "100%", flex: 1, background: "var(--ld-bg)" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px 60px" }}>
          {/* Skeleton Back Button */}
          <div style={{ marginBottom: 24 }}>
            <div className="skeleton" style={{ width: 70, height: 28, borderRadius: 6 }} />
          </div>

          {/* Skeleton Header */}
          <div style={{ marginBottom: 32 }}>
            <div className="skeleton" style={{ width: 110, height: 32, borderRadius: 9999, marginBottom: 16 }} />
            <div className="skeleton" style={{ width: "70%", height: 38, borderRadius: 6, marginBottom: 12 }} />
            <div className="skeleton" style={{ width: "40%", height: 24, borderRadius: 6 }} />
          </div>

          {/* Skeleton Cards Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 24, marginBottom: 24 }}>
            <div className="skeleton" style={{ height: 260, borderRadius: 14 }} />
            <div className="skeleton" style={{ height: 260, borderRadius: 14 }} />
          </div>

          {/* Skeleton Raw Content */}
          <div className="skeleton" style={{ height: 180, borderRadius: 14, marginBottom: 24 }} />

          {/* Skeleton Images */}
          <div className="skeleton" style={{ height: 340, borderRadius: 14 }} />
        </div>
      </div>
    );
  }

  /* ─── Error / Not Found State ─────────────────────── */
  if (error || !leak) {
    return (
      <div className="leak-detail-page enterprise-detail-view" style={{ minHeight: "100%", flex: 1, background: "var(--ld-bg)" }}>
        <div style={{ maxWidth: 800, margin: "80px auto", padding: "0 24px", textAlign: "center" }}>
          <div
            style={{
              background: "var(--ld-card-bg)",
              border: "1px solid rgba(239, 68, 68, 0.2)",
              borderRadius: 16,
              padding: "48px 32px",
              boxShadow: "var(--ld-card-shadow)",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(239, 68, 68, 0.1)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 20,
              }}
            >
              <AlertCircle size={32} color="#ef4444" />
            </div>
            <h2 style={{ fontSize: "1.4rem", fontWeight: 700, color: "var(--ld-title)", marginBottom: 12 }}>
              Leak Record Not Found
            </h2>
            <p style={{ color: "var(--ld-label)", fontSize: "0.92rem", lineHeight: 1.6, maxWidth: 500, margin: "0 auto 28px" }}>
              {error || "The requested threat intelligence record could not be found or you do not have permission to view it."}
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <Link
                to="/leaks"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: "var(--ld-btn-bg)",
                  border: "1px solid var(--ld-btn-border)",
                  color: "var(--ld-btn-text)",
                  padding: "10px 20px",
                  borderRadius: 8,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  transition: "all 0.15s ease",
                }}
              >
                <ArrowLeft size={16} /> Return to Intelligence Feed
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isContentLong = (leak.content || "").length > 400 || (leak.content || "").split("\n").length > 6;
  const singleSector = leak.primary_sector || (leak.sectors && leak.sectors.length > 0 ? leak.sectors[0] : null) || "Unclassified";

  return (
    <div className="leak-detail-page enterprise-detail-view" style={{ minHeight: "100%", flex: 1, background: "var(--ld-bg)", color: "var(--ld-text)", position: "relative" }}>
      {/* ═══ Ambient Glows (Dark Mode Only, Contained) ═══ */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <div
          className="dark-ambient-glow"
          style={{
            position: "absolute",
            top: -120,
            left: -120,
            width: 500,
            height: 500,
            background: "radial-gradient(circle, rgba(99, 102, 241, 0.04) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
        <div
          className="dark-ambient-glow"
          style={{
            position: "absolute",
            top: 300,
            right: -100,
            width: 500,
            height: 500,
            background: "radial-gradient(circle, rgba(6, 182, 212, 0.03) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
      </div>

      {/* ═══ Sticky Navigation Bar ═══ */}
      {isScrolled && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 28px",
            background: "var(--ld-sticky-bg)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderBottom: "1px solid var(--ld-sticky-border)",
            gap: 16,
          }}
          className="enterprise-sticky-header"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: "1 1 auto" }}>
            <Link
              to="/leaks"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "var(--ld-btn-text)",
                textDecoration: "none",
                padding: "6px 12px",
                borderRadius: 6,
                background: "var(--ld-btn-bg)",
                border: "1px solid var(--ld-btn-border)",
                transition: "all 0.15s ease",
              }}
              title="Back to Intelligence Feed"
            >
              <ArrowLeft size={15} />
              <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>Back</span>
            </Link>
            <div style={{ height: 18, width: 1, background: "var(--ld-card-border)" }} />
            <span
              style={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "var(--ld-title)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 700,
              }}
            >
              {leakTitle}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <button
              onClick={handleToggleSave}
              disabled={togglingSave}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                borderRadius: 9999,
                fontSize: "0.68rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s ease",
                color: isSaved ? "#818cf8" : "var(--ld-btn-text)",
                borderColor: isSaved ? "rgba(129, 140, 248, 0.4)" : "var(--ld-btn-border)",
                background: isSaved ? "rgba(129, 140, 248, 0.12)" : "var(--ld-btn-bg)",
                border: "1px solid",
                lineHeight: 1.35,
              }}
            >
              <Bookmark size={11} fill={isSaved ? "currentColor" : "none"} />
              <span>{isSaved ? "Saved" : "Save Post"}</span>
            </button>
            {user?.role === "admin" && leak.source_url && !isUrlscanLeak(leak) && (
              <a
                href={leak.source_url}
                target="_blank"
                rel="noreferrer noopener"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "var(--ld-btn-bg)",
                  border: "1px solid var(--ld-btn-border)",
                  borderRadius: 9999,
                  padding: "6px 14px",
                  color: "var(--ld-btn-text)",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  transition: "all 0.15s ease",
                }}
              >
                View Source <ExternalLink size={13} />
              </a>
            )}
          </div>
        </div>
      )}

      {/* ═══ Main Page Content ═══ */}
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px 60px" }}>
        {/* ── Top Navigation Bar: Only Back ── */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <Link
            to="/leaks"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "var(--ld-back-color)",
              textDecoration: "none",
              fontWeight: 600,
              fontSize: "0.85rem",
              transition: "color 0.15s ease",
              padding: "6px 0",
            }}
          >
            <ArrowLeft size={15} /> Back
          </Link>
        </div>

        {/* ── Title & Header Row ── */}
        <div style={{ position: "relative", marginBottom: 28 }}>
          {/* Save Post Action */}
          <div style={{ marginBottom: 10 }}>
            <button
              onClick={handleToggleSave}
              disabled={togglingSave}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                borderRadius: 9999,
                fontSize: "0.68rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s ease",
                color: isSaved ? "#818cf8" : "var(--ld-btn-text)",
                borderColor: isSaved ? "rgba(129, 140, 248, 0.45)" : "var(--ld-btn-border)",
                background: isSaved ? "rgba(129, 140, 248, 0.12)" : "var(--ld-btn-bg)",
                border: "1px solid",
                lineHeight: 1.35,
              }}
              title={isSaved ? "Remove from saved posts" : "Save post to collection"}
            >
              <Bookmark size={11} fill={isSaved ? "currentColor" : "none"} />
              <span>{isSaved ? "Saved" : "Save Post"}</span>
            </button>
          </div>

          {/* Post ID in gold/amber matching mock */}
          <div
            style={{
              fontSize: "0.82rem",
              fontWeight: 700,
              color: "#f59e0b",
              letterSpacing: "0.08em",
              marginBottom: 8,
              textTransform: "uppercase",
            }}
          >
            POST-{((leak as any).post_id || (leak._id ? (leak._id.length > 16 ? leak._id.slice(-16) : leak._id) : "")).toUpperCase()}
          </div>

          {/* Leak Main Title */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24 }}>
            <h1
              style={{
                fontSize: "1.95rem",
                fontWeight: 700,
                color: "var(--ld-title)",
                lineHeight: 1.28,
                margin: 0,
                letterSpacing: "-0.015em",
                maxWidth: 1050,
                wordBreak: "break-word",
              }}
            >
              {leakTitle}
            </h1>

            {/* Subtle "Post" Watermark on the right */}
            <div
              style={{
                fontSize: "3.2rem",
                fontWeight: 800,
                color: "var(--ld-watermark, rgba(255, 255, 255, 0.08))",
                userSelect: "none",
                letterSpacing: "0.02em",
                lineHeight: 1,
                alignSelf: "center",
              }}
              className="post-watermark hidden sm:block"
            >
              Post
            </div>
          </div>
        </div>

        {/* ═══ TOP TWO-COLUMN GRID: Info & Meta ═══ */}
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 24 }} className="enterprise-top-grid">
          {/* ── Left Column: Info Card ── */}
          <div
            style={{
              background: "var(--ld-card-bg)",
              border: "1px solid var(--ld-card-border)",
              borderRadius: 14,
              padding: "24px 28px",
              boxShadow: "var(--ld-card-shadow)",
              position: "relative",
            }}
            className="enterprise-info-card"
          >
            <h3 style={{ fontSize: "1.1rem", fontWeight: 600, color: "var(--ld-title)", margin: 0, marginBottom: 20, letterSpacing: "-0.01em" }}>
              Info
            </h3>

            <UpgradeBlurOverlay isLocked={isLocked}>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {/* Row 1: Title & Published Date */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 24,
                }}
                className="info-grid-row"
              >
                <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--ld-label)", marginBottom: 4, textTransform: "capitalize" }}>Title</div>
                  <div style={{ fontSize: "0.92rem", color: "var(--ld-title)", lineHeight: 1.45, wordBreak: "break-word" }}>
                    {leakTitle}
                  </div>
                </div>

                <div style={{ minWidth: 140, textAlign: "right", flexShrink: 0, marginLeft: "auto" }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--ld-label)", marginBottom: 4, textTransform: "capitalize" }}>Published on</div>
                  <div style={{ fontSize: "0.92rem", color: "var(--ld-title)", lineHeight: 1.45, fontWeight: 500, whiteSpace: "nowrap" }}>
                    {formatDisplayDate(publishedDateStr)}
                  </div>
                </div>
              </div>

              {/* Row 2: Source URL */}
              <div>
                <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--ld-label)", marginBottom: 4, textTransform: "capitalize" }}>URL</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                  <span
                    style={{
                      fontFamily: "var(--font-mono, monospace)",
                      fontSize: "0.85rem",
                      color: "var(--ld-text)",
                      wordBreak: "break-all",
                    }}
                  >
                    {isLocked ? "http://darknet-source-hidden.onion" : sanitizeDisplayUrl(leak.source_url)}
                  </span>

                  {!isLocked && leak.source_url && (
                    <button
                      onClick={handleCopyUrl}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        background: "none",
                        border: "none",
                        color: "var(--accent-cyan, #06b6d4)",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: "2px 6px",
                        borderRadius: 4,
                        transition: "all 0.15s ease",
                      }}
                      title="Copy source URL"
                    >
                      {urlCopied ? (
                        <>
                          <Check size={13} color="#22c55e" />
                          <span style={{ color: "#22c55e" }}>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy size={13} />
                          <span>Copy Link</span>
                        </>
                      )}
                    </button>
                  )}

                  {user?.role === "admin" && !isLocked && leak.source_url && isSafeUrl(leak.source_url) && !isUrlscanLeak(leak) && (
                    <a
                      href={leak.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "var(--ld-subtle)",
                        display: "inline-flex",
                        alignItems: "center",
                        padding: 4,
                        transition: "color 0.15s ease",
                      }}
                      title="Open source URL (Authorized admin)"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>

              {/* Row 3: Source / Forum & Domain */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 24,
                }}
                className="info-grid-row"
              >
                <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--ld-label)", marginBottom: 4, textTransform: "capitalize" }}>Source / Forum</div>
                  <div style={{ fontSize: "0.92rem", color: "var(--ld-title)", lineHeight: 1.45, marginTop: 4 }}>
                    {getDisplayForum(leak.forum, leak.sectors)}
                  </div>
                  {leak.company_name && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--ld-label)", marginBottom: 4, textTransform: "capitalize" }}>Affected Organization</div>
                      <div style={{ fontSize: "0.92rem", color: "var(--ld-title)", lineHeight: 1.45, display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                        <Building2 size={14} color="var(--ld-subtle)" />
                        <strong>{leak.company_name}</strong>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ minWidth: 140, textAlign: "right", flexShrink: 0, marginLeft: "auto" }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--ld-label)", marginBottom: 4, textTransform: "capitalize" }}>Domain</div>
                  <div style={{ fontSize: "0.92rem", lineHeight: 1.45, marginTop: 4, display: "flex", justifyContent: "flex-end" }}>
                    {resolvedDomain ? (
                      <a
                        href={resolvedDomain.startsWith("http://") || resolvedDomain.startsWith("https://") ? resolvedDomain : `https://${resolvedDomain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          color: "var(--accent-cyan, #06b6d4)",
                          textDecoration: "none",
                          fontWeight: 500,
                          fontSize: "0.88rem",
                          wordBreak: "break-all",
                          transition: "color 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "underline";
                          e.currentTarget.style.color = "var(--accent-blue, #38bdf8)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = "none";
                          e.currentTarget.style.color = "var(--accent-cyan, #06b6d4)";
                        }}
                        title={`Visit https://${resolvedDomain.replace(/^https?:\/\//, "")} (opens in new tab)`}
                      >
                        <span>{resolvedDomain.replace(/^https?:\/\//, "")}</span>
                        <ExternalLink size={12} style={{ flexShrink: 0 }} />
                      </a>
                    ) : (
                      <span style={{ color: "var(--ld-subtle)", fontStyle: "italic", fontSize: "0.85rem" }}>—</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </UpgradeBlurOverlay>
        </div>

        {/* ── Right Column: Meta Card ── */}
        <div
          style={{
            background: "var(--ld-card-bg)",
            border: "1px solid var(--ld-card-border)",
            borderRadius: 14,
            padding: "24px 28px",
            boxShadow: "var(--ld-card-shadow)",
            position: "relative",
          }}
          className="enterprise-meta-card"
        >
          <h3 style={{ fontSize: "1.1rem", fontWeight: 600, color: "var(--ld-title)", margin: 0, marginBottom: 20, letterSpacing: "-0.01em" }}>
            Meta
          </h3>

          <UpgradeBlurOverlay isLocked={isLocked}>
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {/* Field: Category */}
              <div>
                <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--ld-label)", marginBottom: 4, textTransform: "capitalize" }}>Category</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                  {leak.data_types && leak.data_types.length > 0 ? (
                    leak.data_types.map((type) => (
                      <span
                        key={type}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          background: "var(--ld-pill-bg)",
                          border: "1px solid var(--ld-pill-border)",
                          color: "var(--ld-pill-text)",
                          borderRadius: 9999,
                          padding: "4px 14px",
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                        }}
                      >
                        {type.toUpperCase()}
                      </span>
                    ))
                  ) : (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background: "var(--ld-pill-bg)",
                        border: "1px solid var(--ld-pill-border)",
                        color: "var(--ld-pill-text)",
                        borderRadius: 9999,
                        padding: "4px 14px",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                      }}
                    >
                      ALERT
                    </span>
                  )}
                </div>
              </div>

              {/* Field: Network */}
              <div>
                <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--ld-label)", marginBottom: 4, textTransform: "capitalize" }}>Network</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      background: "var(--ld-pill-bg)",
                      border: "1px solid var(--ld-pill-border)",
                      color: "var(--ld-pill-text)",
                      borderRadius: 9999,
                      padding: "4px 14px",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    {leak.network ? leak.network.toUpperCase() : "TOR"}
                  </span>
                </div>
              </div>

              {/* Field: Sector (Only 1 sector name) */}
              <div>
                <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--ld-label)", marginBottom: 4, textTransform: "capitalize" }}>Sector</div>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 6 }}>
                  {singleSector !== "Unclassified" ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        background: "rgba(16, 185, 129, 0.12)",
                        border: "1px solid rgba(16, 185, 129, 0.28)",
                        color: "#10b981",
                        borderRadius: 9999,
                        padding: "4px 14px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10b981" }} />
                      {singleSector}
                    </span>
                  ) : (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background: "var(--ld-btn-bg)",
                        border: "1px solid var(--ld-btn-border)",
                        color: "var(--ld-label)",
                        borderRadius: 9999,
                        padding: "4px 14px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    >
                      Unclassified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </UpgradeBlurOverlay>
        </div>
      </div>

      {/* ═══ SECTION 2: Raw Content ═══ */}
      <div
        style={{
          background: "var(--ld-card-bg)",
          border: "1px solid var(--ld-card-border)",
          borderRadius: 14,
          padding: "24px 28px",
          boxShadow: "var(--ld-card-shadow)",
          position: "relative",
          marginTop: 24,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: "1.1rem", fontWeight: 600, color: "var(--ld-title)", margin: 0, letterSpacing: "-0.01em" }}>
            Raw content
          </h3>
          {!isLocked && leak.content && (
            <button
              onClick={handleCopyContent}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "var(--ld-btn-bg)",
                border: "1px solid var(--ld-btn-border)",
                color: "var(--ld-btn-text)",
                fontSize: "0.78rem",
                fontWeight: 600,
                cursor: "pointer",
                padding: "6px 14px",
                borderRadius: 6,
                transition: "all 0.15s ease",
              }}
              title="Copy raw content to clipboard"
            >
              {contentCopied ? (
                <>
                  <Check size={14} color="#22c55e" />
                  <span style={{ color: "#22c55e" }}>Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={14} />
                  <span>Copy</span>
                </>
              )}
            </button>
          )}
        </div>

        <UpgradeBlurOverlay isLocked={isLocked} minHeight={120}>
          {/* Content Body with Expand/Collapse */}
          <div style={{ position: "relative" }}>
            <div
              style={{
                fontFamily: "var(--font-sans, -apple-system, sans-serif)",
                fontSize: "0.92rem",
                lineHeight: 1.68,
                color: "var(--ld-text)",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: isContentLong && !isContentExpanded && !isLocked ? 240 : "none",
                overflow: isContentLong && !isContentExpanded && !isLocked ? "hidden" : "visible",
                transition: "max-height 0.3s ease",
              }}
            >
              {isLocked ? (
                <div style={{ color: "var(--ld-text, #cbd5e1)", lineHeight: 1.68, fontSize: "0.92rem" }}>
                  <p style={{ margin: "0 0 12px 0" }}>
                    Threat intelligence monitors detected an exfiltration publication by threat actor operations regarding internal infrastructure and database records. The exfiltrated dataset references corporate servers, authentication credentials, employee directory archives, and internal communication logs.
                  </p>
                  <p style={{ margin: 0 }}>
                    Segmented archives have been distributed across darknet mirrors. Full intelligence disclosure, raw SQL exfiltration dumps, unredacted records, and verified indicators of compromise (IOCs) require an active enterprise subscription.
                  </p>
                </div>
              ) : leak.content ? (
                leak.content
              ) : (
                <span style={{ color: "var(--ld-subtle)", fontStyle: "italic" }}>
                  No raw content extracted for this record.
                </span>
              )}
            </div>

            {/* Bottom Fade Mask when collapsed */}
            {isContentLong && !isContentExpanded && !isLocked && (
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 80,
                  background: "var(--ld-fade-gradient)",
                  pointerEvents: "none",
                }}
              />
            )}
          </div>

          {/* Show More / Show Less Button */}
          {isContentLong && !isLocked && (
            <div style={{ marginTop: 12, paddingTop: 10 }}>
              <button
                onClick={() => setIsContentExpanded((prev) => !prev)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "none",
                  border: "none",
                  color: "var(--accent-cyan, #38bdf8)",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "4px 0",
                }}
              >
                <span>{isContentExpanded ? "Show Less" : "Show More"}</span>
                {isContentExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>
            </div>
          )}
        </UpgradeBlurOverlay>
      </div>

        {/* ═══ SECTION 3: Images / Media Gallery ═══ */}
        <div
          style={{
            background: "var(--ld-card-bg)",
            border: "1px solid var(--ld-card-border)",
            borderRadius: 14,
            padding: "24px 28px",
            boxShadow: "var(--ld-card-shadow)",
            position: "relative",
            marginTop: 24,
          }}
        >
          <h3 style={{ fontSize: "1.1rem", fontWeight: 600, color: "var(--ld-title)", margin: 0, marginBottom: 20, letterSpacing: "-0.01em" }}>
            Images
          </h3>

          <UpgradeBlurOverlay isLocked={isLocked} minHeight={160}>
            {isLocked ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                <div
                  style={{
                    position: "relative",
                    maxWidth: 540,
                    width: "100%",
                    height: 240,
                    borderRadius: 12,
                    background: "linear-gradient(145deg, #181920 0%, #0e1015 100%)",
                    border: "1px solid var(--ld-img-border)",
                    boxShadow: "0 6px 28px rgba(0, 0, 0, 0.4)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    padding: 24,
                  }}
                >
                  <FileImage size={42} style={{ opacity: 0.35, color: "var(--accent-cyan, #06b6d4)" }} />
                  <div style={{ height: 14, width: 160, background: "var(--ld-title, #fff)", borderRadius: 4, opacity: 0.5 }} />
                  <div style={{ height: 12, width: 220, background: "var(--ld-subtle, #94a3b8)", borderRadius: 4, opacity: 0.35 }} />
                </div>
              </div>
            ) : currentScreenshotUrl ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                {/* Centered Main Image Container */}
                <div
                  onClick={() => {
                    setIsLightboxOpen(true);
                    setZoomLevel(1);
                  }}
                  style={{
                    position: "relative",
                    maxWidth: 540,
                    width: "100%",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "var(--ld-img-bg)",
                    border: "1px solid var(--ld-img-border)",
                    boxShadow: "0 6px 28px rgba(0, 0, 0, 0.4)",
                    cursor: isLocked ? "default" : "pointer",
                  }}
                  className="enterprise-image-preview"
                  title={isLocked ? undefined : "Click to enlarge image"}
                >
                  <img
                    src={currentScreenshotUrl}
                    alt="Visual Evidence"
                    onError={() => {
                      if (allScreenshots.length > 1) {
                        const nextIdx = (activeScreenshotIdx + 1) % allScreenshots.length;
                        if (nextIdx !== activeScreenshotIdx) {
                          setActiveScreenshotIdx(nextIdx);
                          return;
                        }
                      }
                      setIsScreenshotUnavailable(true);
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      maxHeight: 520,
                      objectFit: "contain",
                      borderRadius: 10,
                    }}
                    loading="lazy"
                  />

                  {/* Hover overlay with zoom icon */}
                  {!isLocked && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(0, 0, 0, 0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.2s ease",
                      }}
                      className="image-hover-overlay"
                    >
                      <div
                        style={{
                          background: "rgba(0, 0, 0, 0.75)",
                          backdropFilter: "blur(6px)",
                          borderRadius: 30,
                          padding: "8px 16px",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          color: "#ffffff",
                          fontSize: "0.82rem",
                          fontWeight: 600,
                        }}
                      >
                        <ZoomIn size={16} /> Click to Enlarge
                      </div>
                    </div>
                  )}
                </div>

                {/* Thumbnails Row (if multiple images or current) */}
                <div style={{ display: "flex", gap: 10, overflowX: "auto", padding: "6px 4px", justifyContent: "center" }}>
                  {allScreenshots.map((src, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        if (isLocked) return;
                        setActiveScreenshotIdx(idx);
                        setIsScreenshotUnavailable(false);
                      }}
                      style={{
                        width: 84,
                        height: 60,
                        borderRadius: 8,
                        overflow: "hidden",
                        cursor: isLocked ? "default" : "pointer",
                        border: activeScreenshotIdx === idx ? "2px solid #06b6d4" : "1px solid var(--ld-img-border)",
                        opacity: activeScreenshotIdx === idx ? 1 : 0.45,
                        transition: "all 0.2s ease",
                        background: "var(--ld-img-bg)",
                      }}
                    >
                      <img
                        src={src}
                        alt={`Thumbnail ${idx + 1}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>

                {/* Caption / Source Metadata */}
                <p style={{ fontSize: "0.78rem", color: "var(--ld-subtle)", margin: 0, textAlign: "center" }}>
                  Evidence captured from <strong>{getDisplayForum(leak.forum, leak.sectors)}</strong>
                  {publishedDateStr && ` • ${formatDisplayDate(publishedDateStr)}`}
                </p>
              </div>
            ) : (
              /* Empty State */
              <div
                style={{
                  background: "var(--ld-empty-bg)",
                  border: "1px dashed var(--ld-empty-border)",
                  borderRadius: 12,
                  padding: "48px 20px",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ImageIcon size={38} color="var(--ld-subtle)" />
                <p style={{ margin: "12px 0 0", fontSize: "0.88rem", color: "var(--ld-label)", fontWeight: 500 }}>
                  No visual evidence or screenshots attached to this leak record.
                </p>
              </div>
            )}
          </UpgradeBlurOverlay>
        </div>

        {/* ═══ SECTION 5: External Downloads ═══ */}
        {leak.all_download_links && leak.all_download_links.length > 0 && (
          <div
            style={{
              background: "var(--ld-card-bg)",
              border: "1px solid var(--ld-card-border)",
              borderRadius: 14,
              padding: "24px 28px",
              boxShadow: "var(--ld-card-shadow)",
              position: "relative",
              marginTop: 24,
            }}
          >
            <UpgradeBlurOverlay isLocked={isLocked} minHeight={120}>
              {/* Warning Banner */}
              <div
                style={{
                  background: "rgba(249, 115, 22, 0.08)",
                  border: "1px solid rgba(249, 115, 22, 0.2)",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 16,
                  fontSize: "0.8rem",
                  color: "#fb923c",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  lineHeight: 1.5,
                }}
              >
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  <strong>Security Advisory:</strong> These links originate from malicious threat actor infrastructure. Always use an isolated sandbox environment.
                </div>
              </div>

              {/* Links List */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(isLocked
                  ? [
                      "https://darknet-archive-mirror.onion/archive_part1.tar.gz",
                      "https://darknet-archive-mirror.onion/archive_part2.tar.gz",
                    ]
                  : leak.all_download_links
                ).map((link, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "var(--ld-dl-bg)",
                      border: "1px solid var(--ld-dl-border)",
                      borderRadius: 8,
                      padding: "10px 16px",
                      gap: 12,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "var(--font-mono, monospace)",
                        fontSize: "0.82rem",
                        color: "var(--ld-dl-text)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {link}
                    </span>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {!isLocked && (
                        <button
                          onClick={async () => {
                            await navigator.clipboard.writeText(link);
                            toast.success("Download link copied");
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            background: "none",
                            border: "none",
                            color: "var(--accent-cyan, #06b6d4)",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            padding: "2px 6px",
                            borderRadius: 4,
                            transition: "all 0.15s ease",
                          }}
                          title="Copy link"
                        >
                          <Copy size={13} /> Copy
                        </button>
                      )}
                      {!isLocked && (
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: "0.72rem",
                            color: "#06b6d4",
                            fontWeight: 700,
                            padding: "4px 10px",
                            borderRadius: 4,
                            textDecoration: "none",
                            background: "rgba(6, 182, 212, 0.1)",
                            border: "1px solid rgba(6, 182, 212, 0.2)",
                            transition: "all 0.15s ease",
                          }}
                          title="Open external link"
                        >
                          OPEN <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </UpgradeBlurOverlay>
          </div>
        )}
      </div>

      {/* ═══ Lightbox Modal ═══ */}
      {isLightboxOpen && currentScreenshotUrl && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0, 0, 0, 0.94)",
            backdropFilter: "blur(12px)",
            cursor: "pointer",
            padding: 24,
          }}
          onClick={() => {
            setIsLightboxOpen(false);
            setZoomLevel(1);
          }}
        >
          {/* Close button */}
          <button
            style={{
              position: "fixed",
              top: 24,
              right: 24,
              background: "rgba(255, 255, 255, 0.1)",
              border: "none",
              borderRadius: "50%",
              width: 44,
              height: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              cursor: "pointer",
              transition: "all 0.15s ease",
              zIndex: 1310,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsLightboxOpen(false);
              setZoomLevel(1);
            }}
            title="Close (Esc)"
          >
            <X size={24} />
          </button>

          {/* Image */}
          <div
            ref={lightboxContainerRef}
            style={{
              maxWidth: "92vw",
              maxHeight: "88vh",
              width: "92vw",
              height: "88vh",
              overflow: zoomLevel > 1 ? "auto" : "hidden",
              display: "flex",
              alignItems: zoomLevel > 1 ? "flex-start" : "center",
              justifyContent: zoomLevel > 1 ? "flex-start" : "center",
              overflowAnchor: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentScreenshotUrl}
              alt="Visual Evidence Full Preview"
              style={{
                width: zoomLevel === 1 ? "auto" : `${100 * zoomLevel}%`,
                maxWidth: zoomLevel === 1 ? "90vw" : "none",
                maxHeight: zoomLevel === 1 ? "85vh" : "none",
                objectFit: "contain",
                borderRadius: 8,
                boxShadow: "0 12px 48px rgba(0,0,0,0.8)",
                display: "block",
                margin: zoomLevel > 1 ? 0 : "auto",
              }}
            />
          </div>

          {/* Floating Zoom Controls Bar */}
          <div
            style={{
              position: "fixed",
              bottom: 30,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "rgba(24, 24, 27, 0.9)",
              backdropFilter: "blur(12px)",
              padding: "8px 18px",
              borderRadius: 30,
              border: "1px solid rgba(255, 255, 255, 0.15)",
              zIndex: 1310,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.6)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setZoomLevel((z) => Math.max(0.25, z - 0.25))}
              style={{
                background: "rgba(255, 255, 255, 0.08)",
                border: "none",
                borderRadius: 6,
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              title="Zoom Out (-)"
            >
              <ZoomOut size={18} />
            </button>
            <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#ffffff", minWidth: 46, textAlign: "center" }}>
              {Math.round(zoomLevel * 100)}%
            </span>
            <button
              onClick={() => setZoomLevel((z) => Math.min(4, z + 0.25))}
              style={{
                background: "rgba(255, 255, 255, 0.08)",
                border: "none",
                borderRadius: 6,
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              title="Zoom In (+)"
            >
              <ZoomIn size={18} />
            </button>
            <div style={{ width: 1, height: 20, background: "rgba(255, 255, 255, 0.15)", margin: "0 4px" }} />
            <button
              onClick={() => {
                setZoomLevel(1);
                // Reset scroll position to origin when zoom resets
                if (lightboxContainerRef.current) {
                  lightboxContainerRef.current.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                }
              }}
              style={{
                background: "rgba(255, 255, 255, 0.08)",
                border: "none",
                borderRadius: 6,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                cursor: "pointer",
                transition: "all 0.15s ease",
                fontSize: "0.75rem",
                fontWeight: 600,
                padding: "0 10px",
                width: "auto",
              }}
              title="Reset Zoom"
            >
              RESET
            </button>
          </div>
        </div>
      )}

      {/* ═══ Scoped Theme Variables & CSS ═══ */}
      <style>{`
        .enterprise-detail-view {
          --ld-bg: #000000;
          --ld-card-bg: #0d0d0f;
          --ld-card-border: rgba(255, 255, 255, 0.08);
          --ld-card-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
          --ld-title: #ffffff;
          --ld-text: #e4e4e7;
          --ld-label: #71717a;
          --ld-subtle: rgba(255, 255, 255, 0.45);
          --ld-pill-bg: #18181b;
          --ld-pill-border: #3f3f46;
          --ld-pill-text: #f4f4f5;
          --ld-btn-bg: #18181b;
          --ld-btn-border: rgba(255, 255, 255, 0.15);
          --ld-btn-text: #e4e4e7;
          --ld-back-color: rgba(255, 255, 255, 0.85);
          --ld-sticky-bg: rgba(10, 10, 12, 0.92);
          --ld-sticky-border: rgba(255, 255, 255, 0.08);
          --ld-fade-gradient: linear-gradient(to bottom, transparent, #0d0d0f);
          --ld-img-bg: #000000;
          --ld-img-border: rgba(255, 255, 255, 0.1);
          --ld-empty-bg: #08080a;
          --ld-empty-border: rgba(255, 255, 255, 0.12);
          --ld-dl-bg: #121215;
          --ld-dl-border: rgba(255, 255, 255, 0.06);
          --ld-dl-text: rgba(255, 255, 255, 0.7);
          --ld-watermark: rgba(255, 255, 255, 0.05);
        }

        body.light-theme .enterprise-detail-view {
          --ld-bg: #f8fafc;
          --ld-card-bg: #ffffff;
          --ld-card-border: #e2e8f0;
          --ld-card-shadow: 0 2px 14px rgba(0, 0, 0, 0.05);
          --ld-title: #0f172a;
          --ld-text: #334155;
          --ld-label: #64748b;
          --ld-subtle: #94a3b8;
          --ld-pill-bg: #f1f5f9;
          --ld-pill-border: #cbd5e1;
          --ld-pill-text: #0f172a;
          --ld-btn-bg: #ffffff;
          --ld-btn-border: #cbd5e1;
          --ld-btn-text: #0f172a;
          --ld-back-color: #0f172a;
          --ld-sticky-bg: rgba(255, 255, 255, 0.94);
          --ld-sticky-border: #e2e8f0;
          --ld-fade-gradient: linear-gradient(to bottom, transparent, #ffffff);
          --ld-img-bg: #f8fafc;
          --ld-img-border: #e2e8f0;
          --ld-empty-bg: #f8fafc;
          --ld-empty-border: #cbd5e1;
          --ld-dl-bg: #f8fafc;
          --ld-dl-border: #e2e8f0;
          --ld-dl-text: #334155;
          --ld-watermark: rgba(15, 23, 42, 0.05);
        }

        body.light-theme .enterprise-detail-view .dark-ambient-glow {
          display: none !important;
        }

        @media (min-width: 1024px) {
          .post-watermark {
            display: block !important;
          }
        }
        @media (max-width: 900px) {
          .enterprise-top-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 640px) {
          .info-grid-row {
            flex-direction: column !important;
            gap: 12px !important;
          }
          .info-grid-row > div:last-child {
            text-align: left !important;
            margin-left: 0 !important;
            justify-content: flex-start !important;
          }
        }
        .enterprise-image-preview:hover .image-hover-overlay {
          opacity: 1 !important;
        }
        .skeleton {
          background: linear-gradient(90deg, #18181b 25%, #27272a 50%, #18181b 75%);
          background-size: 200% 100%;
          animation: skeleton-shimmer 1.5s infinite;
        }
        body.light-theme .skeleton {
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
          background-size: 200% 100%;
        }
        @keyframes skeleton-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
